// recentList — the list pattern for the left menu's recent items (pen
// "Projects and Studio", decision 3, decided 2026-10-20), pure.
//
// What the author chose for one list (`RecentListView`): which items are
// pinned (in pin order, newest pin last), which were removed from Recent,
// the order they were opened in, the sort and the density. The host keeps it
// per device (recentListPreference.ts) and advances it only through
// `foldRecentList`; the menu projects it with `recentSectionsOf`.
//
// Generic over the item: anything with an id, a title and an optional
// last-changed instant, so projects can plug in when they exist.

export type { RecentDensity, RecentListCommand, RecentListView, RecentSort };
export {
    DEFAULT_RECENT_LIST,
    foldRecentList,
    parseRecentList,
    RECENT_SORTS,
    recentSectionsOf,
    serializeRecentList,
};

import { exhaustSwitchCase } from "@bilda/utils";

type RecentSort = "last-opened" | "last-changed" | "name";
type RecentDensity = "compact" | "detailed";

type RecentListView = {
    readonly sort: RecentSort;
    readonly density: RecentDensity;
    // Pin order: the newest pin last.
    readonly pinned: readonly string[];
    // Removed from Recent; opening the item again brings it back.
    readonly hidden: readonly string[];
    // The most recently opened first (capped at OPENED_MAX).
    readonly opened: readonly string[];
};

// One command per intent.
type RecentListCommand =
    | { readonly type: "pin"; readonly id: string }
    | { readonly type: "unpin"; readonly id: string }
    | { readonly type: "remove-from-recent"; readonly id: string }
    | { readonly type: "opened"; readonly id: string }
    | { readonly type: "sort-by"; readonly sort: RecentSort }
    | { readonly type: "show-density"; readonly density: RecentDensity };

type Sortable = {
    readonly id: string;
    readonly title: string;
    // ISO 8601; absent sorts last.
    readonly changedAt?: string;
};

type RecentSections<T> = {
    readonly pinned: readonly T[];
    readonly recent: readonly T[];
    // More items exist than the section shows.
    readonly morePinned: boolean;
    readonly moreRecent: boolean;
};

// At most this many rows per section (then "Show all …" when a destination
// exists).
const RECENT_MAX = 5;
// How many opens are remembered (enough to order any list the menu shows).
const OPENED_MAX = 200;

const DEFAULT_RECENT_LIST: RecentListView = {
    sort: "last-opened",
    density: "compact",
    pinned: [],
    hidden: [],
    opened: [],
};

const without = (ids: readonly string[], id: string): readonly string[] =>
    ids.filter((x) => x !== id);

const foldRecentList = (
    view: RecentListView,
    cmd: RecentListCommand,
): RecentListView => {
    switch (cmd.type) {
        case "pin":
            return view.pinned.includes(cmd.id)
                ? view
                : { ...view, pinned: [...view.pinned, cmd.id] };
        case "unpin":
            return { ...view, pinned: without(view.pinned, cmd.id) };
        case "remove-from-recent":
            return view.hidden.includes(cmd.id)
                ? view
                : { ...view, hidden: [...view.hidden, cmd.id] };
        case "opened":
            return view.opened[0] === cmd.id && !view.hidden.includes(cmd.id)
                ? view
                : {
                      ...view,
                      hidden: without(view.hidden, cmd.id),
                      opened: [cmd.id, ...without(view.opened, cmd.id)].slice(
                          0,
                          OPENED_MAX,
                      ),
                  };
        case "sort-by":
            return view.sort === cmd.sort ? view : { ...view, sort: cmd.sort };
        case "show-density":
            return view.density === cmd.density
                ? view
                : { ...view, density: cmd.density };
        default:
            return exhaustSwitchCase(cmd);
    }
};

// Last changed, newest first; no instant sorts last (ISO 8601 UTC compares
// chronologically as text).
const byChanged = (a: Sortable, b: Sortable): number =>
    (b.changedAt ?? "").localeCompare(a.changedAt ?? "");

const sortedBy = <T extends Sortable>(
    items: readonly T[],
    view: RecentListView,
    locale: string,
): readonly T[] => {
    switch (view.sort) {
        case "last-changed":
            return [...items].sort(byChanged);
        case "name":
            return [...items].sort((a, b) =>
                a.title.localeCompare(b.title, locale, { sensitivity: "base" }),
            );
        case "last-opened": {
            // Opened ones in open order; never-opened ones after, by change.
            const rank = (id: string): number => {
                const at = view.opened.indexOf(id);
                return at === -1 ? Number.POSITIVE_INFINITY : at;
            };
            return [...items].sort((a, b) => {
                const ra = rank(a.id);
                const rb = rank(b.id);
                return ra === rb ? byChanged(a, b) : ra < rb ? -1 : 1;
            });
        }
        default:
            return exhaustSwitchCase(view.sort);
    }
};

// Pinned first in pin order (never repeated below), then the rest by the
// chosen sort minus what was removed from Recent; each capped at `max`.
const recentSectionsOf = <T extends Sortable>(
    items: readonly T[],
    view: RecentListView,
    locale: string,
    max: number = RECENT_MAX,
): RecentSections<T> => {
    const pinned = view.pinned.flatMap((id) =>
        items.filter((item) => item.id === id),
    );
    const rest = sortedBy(
        items.filter(
            (item) =>
                !view.pinned.includes(item.id) &&
                !view.hidden.includes(item.id),
        ),
        view,
        locale,
    );
    return {
        pinned: pinned.slice(0, max),
        recent: rest.slice(0, max),
        morePinned: pinned.length > max,
        moreRecent: rest.length > max,
    };
};

// === Persistence (parse, don't validate) ====================================
// Stored as JSON by any earlier build; anything unknown falls back to the
// default, field by field.

// In the order the View options menu lists them.
const RECENT_SORTS: readonly RecentSort[] = [
    "last-opened",
    "last-changed",
    "name",
];
const DENSITIES: readonly RecentDensity[] = ["compact", "detailed"];

const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === "object" && v !== null && !Array.isArray(v);

const idsOf = (v: unknown): readonly string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

const parseJson = (raw: string): unknown => {
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

// `defaults`: the surface's own (the Projects page starts Detailed).
const parseRecentList = (
    raw: string | null,
    defaults: RecentListView = DEFAULT_RECENT_LIST,
): RecentListView => {
    const data = raw === null ? null : parseJson(raw);
    if (!isRecord(data)) return defaults;
    return {
        sort: RECENT_SORTS.find((s) => s === data.sort) ?? defaults.sort,
        density: DENSITIES.find((d) => d === data.density) ?? defaults.density,
        pinned: idsOf(data.pinned),
        hidden: idsOf(data.hidden),
        opened: idsOf(data.opened).slice(0, OPENED_MAX),
    };
};

const serializeRecentList = (view: RecentListView): string =>
    JSON.stringify(view);
